import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { errorMessageHandler } from '../../../PhishingManager/utils';
import { Icon } from 'Components';
import { useIsMobile } from 'Hooks';
import { useStores } from 'Hooks/useStore';
import { TemplateDetails } from 'PhishingManager/pages/CampaignSteps';
import { SystemRoleType } from 'Services/Api/Roles/Types';
import TemplateActionModal, { TemplateActionType } from '../TemplateActionModal';
import ThumbnailUploader from './ThumbnailUploader';
import { updatePackagedTemplate } from '../../../PhishingManager/service/index.Service';
import {
  ValidationErrors,
  FALLBACK_PREVIEW_IMG,
  validatePrefilledForm,
  parseBackendErrors
} from './validation';
import styles from './PrefilledForm.module.scss';

export interface PrefilledFormProps {
  onBack: (toastData?: { message: string; type: 'warning' | 'success' }) => void;
  setToast: (toast: { message: string; type: 'warning' | 'success' }) => void;
  templateDetails?: TemplateDetails | null;
  isPublished?: boolean;
}

export const PrefilledForm: React.FC<PrefilledFormProps> = ({
  onBack,
  setToast,
  templateDetails,
  isPublished = false
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { authStore } = useStores();

  const topRef = useRef<HTMLDivElement>(null);

  const DIFFICULTY_OPTIONS = [
    { label: t('phishingTemplateManager.difficultyOptions.beginner'), value: 'beginner' },
    { label: t('phishingTemplateManager.difficultyOptions.intermediate'), value: 'intermediate' },
    { label: t('phishingTemplateManager.difficultyOptions.advanced'), value: 'advanced' }
  ];

  const CATEGORY_OPTIONS = [
    { label: t('phishingTemplateManager.categoryOptions.aiDriven'), value: '1' },
    { label: t('phishingTemplateManager.categoryOptions.hospitalitySpecific'), value: '2' },
    { label: t('phishingTemplateManager.categoryOptions.coreEssentials'), value: '3' }
  ];

  useEffect(() => {
    const resetScroll = () => {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      
      if (topRef.current) {
        let parent = topRef.current.parentElement;
        while (parent) {
          parent.scrollTop = 0;
          parent = parent.parentElement;
        }
      }
    };

    resetScroll();
    const timer = setTimeout(resetScroll, 50);
    return () => clearTimeout(timer);
  }, []);

  const currentUser = authStore?.currentUser;
  const primaryRole = currentUser?.primarySystemRole;
  const roleName = primaryRole?.role || 'No Role Found';
  const roleId = primaryRole?.id;
  const firstGroupId = currentUser?.groups?.[0]?.id;
  const originalTenantId = (currentUser as any)?.venza_tenant_id;

  const isSuperAdmin =
    roleName === SystemRoleType.SuperAdmin ||
    roleName === 'superadmin' ||
    String(roleName).toLowerCase() === 'superadmin' ||
    roleId === 1;

  const evaluatedTenantId = isSuperAdmin ? 0 : (firstGroupId ?? originalTenantId ?? 0);

  const [name, setName] = useState<string>('');
  const [difficulty, setDifficulty] = useState<string>('beginner');
  const [selectedCategory, setSelectedCategory] = useState<string>('1');
  const [description, setDescription] = useState<string>('');

  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>('');
  const [imageRemoved, setImageRemoved] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [modalType, setModalType] = useState<TemplateActionType>('publish');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (templateDetails) {
      setName(templateDetails.name || '');
      setDifficulty(templateDetails.difficulty?.toLowerCase() || 'beginner');
      setDescription(templateDetails.description || '');

      if (templateDetails.categories && templateDetails.categories.length > 0) {
        setSelectedCategory(String(templateDetails.categories[0].id));
      }

      const initialImg =
        templateDetails.image_path ||
        (templateDetails as any)?.img_path ||
        FALLBACK_PREVIEW_IMG;
      setImagePreviewUrl(initialImg);
      setSelectedImageFile(null);
      setImageRemoved(false);
    }
  }, [templateDetails]);

  const isFormDirty = useMemo(() => {
    if (!templateDetails) return false;

    const initialName = templateDetails.name || '';
    const initialDifficulty = templateDetails.difficulty?.toLowerCase() || 'beginner';
    const initialDescription = templateDetails.description || '';
    const initialCategory =
      templateDetails.categories && templateDetails.categories.length > 0
        ? String(templateDetails.categories[0].id)
        : '1';

    return (
      name.trim() !== initialName.trim() ||
      difficulty !== initialDifficulty ||
      selectedCategory !== initialCategory ||
      description.trim() !== initialDescription.trim() ||
      selectedImageFile !== null ||
      imageRemoved
    );
  }, [
    templateDetails,
    name,
    difficulty,
    selectedCategory,
    description,
    selectedImageFile,
    imageRemoved
  ]);

  const isFormValid = useMemo(() => {
    const hasName = name.trim().length > 0;
    const hasDifficulty = Boolean(difficulty);
    const hasCategory = Boolean(selectedCategory);
    const hasDescription = description.trim().length > 0;
    const hasImage = !imageRemoved && (Boolean(imagePreviewUrl) || Boolean(selectedImageFile));

    return hasName && hasDifficulty && hasCategory && hasDescription && hasImage;
  }, [name, difficulty, selectedCategory, description, imageRemoved, imagePreviewUrl, selectedImageFile]);

  const tenantAssigned = (templateDetails as any)?.target_tenant || t('phishingTemplateManager.prefilledForm.allTenants');
  const languagesList =
    templateDetails?.languages?.map((lang) => lang.name).join(', ') || 'English';

  const sendingProfileName =
    (templateDetails?.languages && (templateDetails.languages[0] as any)?.sending_profile_name) ||
    'Venza Default Profile';

  const phishingDomainName =
    (templateDetails?.languages && (templateDetails.languages[0] as any)?.phishing_domain_name) ||
    'stage-phishing.venzapeak.com';

  const scrollToError = () => {
    setTimeout(() => {
      const errorElement = document.querySelector('.has-error, [class*="hasError"]');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleValidateForm = useCallback((): boolean => {
    const newErrors = validatePrefilledForm({
      name,
      description,
      selectedImageFile,
      t
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, description, selectedImageFile, t]);

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImageFile(file);
      setImageRemoved(false);
      const preview = URL.createObjectURL(file);
      setImagePreviewUrl(preview);
      setErrors((prev) => ({ ...prev, image_path: undefined }));
    }
  };

  const handleTriggerFileInput = () => {
    if (!isSuperAdmin) return;
    fileInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    if (!isSuperAdmin) return;
    setSelectedImageFile(null);
    setImageRemoved(true);
    setImagePreviewUrl('');
  };

  const handleOpenPublishModal = () => {
    if (!isSuperAdmin || !isFormValid) return;
    if (!handleValidateForm()) {
      scrollToError();
      return;
    }
    setModalType('publish');
    setIsModalVisible(true);
  };

  const handleOpenSaveChangesModal = () => {
    if (!isSuperAdmin || !isFormValid) return;

    if (!isFormDirty) {
      setToast({
        message: t('phishingTemplateManager.prefilledForm.noChangesDetected', 'No changes detected to save.'),
        type: 'warning'
      });
      return;
    }

    if (!handleValidateForm()) {
      scrollToError();
      return;
    }
    setModalType('save_changes');
    setIsModalVisible(true);
  };

  const handleOpenUnpublishModal = () => {
    if (!isSuperAdmin) return;
    setModalType('unpublish');
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const handleConfirmAction = async () => {
    if (!templateDetails?.id) return;

    try {
      setIsSubmitting(true);
      setErrors({});

      let payload: FormData | Record<string, any>;

      if (modalType === 'unpublish') {
        payload = {
          venza_tenant_id: Number(evaluatedTenantId),
          status: 'inactive'
        };
      } else if (selectedImageFile) {
        const formData = new FormData();
        formData.append('_method', 'PATCH');
        formData.append('venza_tenant_id', String(evaluatedTenantId));
        formData.append('name', name.trim());
        formData.append('difficulty', difficulty);
        if (description.trim()) {
          formData.append('description', description.trim());
        }
        formData.append('status', modalType === 'publish' ? 'active' : (isPublished ? 'active' : 'inactive'));
        formData.append('categories[0]', String(selectedCategory));
        formData.append('image_path', selectedImageFile);
        payload = formData;
      } else {
        payload = {
          venza_tenant_id: Number(evaluatedTenantId),
          name: name.trim(),
          difficulty: difficulty,
          description: description.trim(),
          status: modalType === 'publish' ? 'active' : (isPublished ? 'active' : 'inactive'),
          categories: [Number(selectedCategory)]
        };
      }

      const res: any = await updatePackagedTemplate(templateDetails.id, payload);

      if (res && res.status === 'error') {
        if (res.errors) {
          const { fieldErrors, generalError } = parseBackendErrors(res.errors);
          setErrors(fieldErrors);
          scrollToError();
          if (generalError || res.message) {
            setToast({
              message: generalError || res.message,
              type: 'warning'
            });
          }
        } else {
          setToast({
            message: res.message || t('errorCodes.genericErrorMessage'),
            type: 'warning'
          });
        }
        setIsModalVisible(false);
        return;
      }

      const defaultSuccessMessage =
        modalType === 'publish'
          ? t('phishingTemplateManager.prefilledForm.publishedSuccess')
          : modalType === 'unpublish'
          ? t('phishingTemplateManager.prefilledForm.unpublishedSuccess')
          : t('phishingTemplateManager.prefilledForm.updatedSuccess');

      setIsModalVisible(false);
      onBack({
        message: defaultSuccessMessage,
        type: 'success'
      });
    } catch (error: any) {
      console.error(`Failed to execute ${modalType}:`, error);
      const backendErrors = error?.response?.data?.errors || error?.errors;
      const backendMessage = error?.response?.data?.message || error?.message;

      if (backendErrors) {
        const { fieldErrors, generalError } = parseBackendErrors(backendErrors);
        setErrors(fieldErrors);
        scrollToError();
        setToast({
          message: generalError || backendMessage || t('errorCodes.genericErrorMessage'),
          type: 'warning'
        });
      } else if (backendMessage) {
        setToast({
          message: backendMessage,
          type: 'warning'
        });
      } else {
        const toastMessage = errorMessageHandler(error, 'phishingManager.errors.updateTemplate');
        setToast({
          message: t(toastMessage),
          type: 'warning'
        });
      }
      setIsModalVisible(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageTitle = isPublished
    ? t('phishingTemplateManager.prefilledForm.editTemplateTitle')
    : t('phishingTemplateManager.prefilledForm.editPublishTitle');

  const pageSubtitle = isPublished
    ? t('phishingTemplateManager.prefilledForm.editTemplateSubtitle')
    : t('phishingTemplateManager.prefilledForm.editPublishSubtitle');

  return (
    <div ref={topRef} className={styles.container}>
      <div className={styles.headerBlock}>
        <div className={styles.titleRow}>
          <button
            type="button"
            onClick={() => onBack()}
            className={styles.backButton}
            title={t('phishingManager.back')}
          >
            <Icon type="chevronLeft" />
          </button>
          <h1 className={styles.headerTitle}>{pageTitle}</h1>
        </div>
        <p className={styles.headerDescription}>{pageSubtitle}</p>
      </div>

      <div className={styles.formCard}>
        <div className={styles.section}>
          <div className={styles.sectionGroup}>
            <div className={styles.sectionTitle}>
              {t('phishingTemplateManager.prefilledForm.basicInformation')}
            </div>

            <div className={`${styles.basicInfoRow} ${isMobile ? styles.mobile : ''}`}>
              <div className={`${styles.nameFieldGroup} ${isMobile ? styles.mobile : ''}`}>
                <label className={styles.label}>
                  {t('phishingTemplateManager.prefilledForm.templateName')}{' '}
                  <span className={styles.requiredAsterisk}>*</span>
                </label>
                <div
                  className={`${styles.inputWrapper} ${!isSuperAdmin ? styles.disabled : ''} ${
                    errors.name ? styles.hasError : ''
                  }`}
                >
                  <input
                    type="text"
                    disabled={!isSuperAdmin}
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                    }}
                    className={styles.textInput}
                  />
                </div>
                {errors.name && <span className={styles.fieldError}>{errors.name}</span>}
              </div>

              <div className={`${styles.flexSelectGroup} ${isMobile ? styles.mobile : ''}`}>
                <label className={styles.label}>
                  {t('phishingTemplateManager.prefilledForm.difficulty')}{' '}
                  <span className={styles.requiredAsterisk}>*</span>
                </label>
                <Select
                  className={`prefilled-form-select campaign-setup-select ${errors.difficulty ? 'has-error' : ''}`}
                  dropdownClassName="campaign-setup-filter-option-select"
                  suffixIcon={<Icon type="chevronDown" />}
                  disabled={!isSuperAdmin}
                  value={difficulty}
                  onChange={(value) => {
                    setDifficulty(value);
                    if (errors.difficulty) setErrors((prev) => ({ ...prev, difficulty: undefined }));
                  }}
                  options={DIFFICULTY_OPTIONS}
                  getPopupContainer={(trigger) => trigger.parentElement || document.body}
                  style={{ width: '100%', height: '36px' }}
                />
                {errors.difficulty && <span className={styles.fieldError}>{errors.difficulty}</span>}
              </div>

              <div className={`${styles.flexSelectGroup} ${isMobile ? styles.mobile : ''}`}>
                <label className={styles.label}>
                  {t('phishingTemplateManager.prefilledForm.category')}{' '}
                  <span className={styles.requiredAsterisk}>*</span>
                </label>
                <Select
                  className={`prefilled-form-select campaign-setup-select ${errors.categories ? 'has-error' : ''}`}
                  dropdownClassName="campaign-setup-filter-option-select"
                  suffixIcon={<Icon type="chevronDown" />}
                  disabled={!isSuperAdmin}
                  value={selectedCategory}
                  onChange={(value) => {
                    setSelectedCategory(value);
                    if (errors.categories) setErrors((prev) => ({ ...prev, categories: undefined }));
                  }}
                  options={CATEGORY_OPTIONS}
                  getPopupContainer={(trigger) => trigger.parentElement || document.body}
                  style={{ width: '100%', height: '36px' }}
                />
                {errors.categories && <span className={styles.fieldError}>{errors.categories}</span>}
              </div>
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>
              {t('phishingTemplateManager.prefilledForm.description')}{' '}
              <span className={styles.requiredAsterisk}>*</span>
            </label>
            <div
              className={`${styles.textareaWrapper} ${!isSuperAdmin ? styles.disabled : ''} ${
                errors.description ? styles.hasError : ''
              }`}
            >
              <textarea
                disabled={!isSuperAdmin}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (errors.description) setErrors((prev) => ({ ...prev, description: undefined }));
                }}
                rows={3}
                className={styles.textareaInput}
              />
            </div>
            {errors.description && <span className={styles.fieldError}>{errors.description}</span>}
          </div>

          <div className={styles.fieldGroup}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageFileChange}
              accept="image/jpeg,image/png,image/jpg,image/gif"
              style={{ display: 'none' }}
            />

            <label className={styles.label}>
              {t('phishingTemplateManager.prefilledForm.thumbnailImage')}{' '}
              <span className={styles.requiredAsterisk}>*</span>
            </label>

            {imageRemoved || (!imagePreviewUrl && !selectedImageFile) ? (
              <ThumbnailUploader
                isSuperAdmin={isSuperAdmin}
                hasError={Boolean(errors.image_path)}
                onTriggerFileInput={handleTriggerFileInput}
              />
            ) : (
              <div
                className={`${styles.thumbnailCard} ${!isSuperAdmin ? styles.disabled : ''} ${
                  errors.image_path ? styles.hasError : ''
                }`}
              >
                <div className={styles.thumbnailInfo}>
                  <img src={imagePreviewUrl} alt="Thumbnail" className={styles.thumbnailImage} />
                  <div className={styles.thumbnailMeta}>
                    <span className={styles.thumbnailName}>
                      {selectedImageFile ? selectedImageFile.name : (name || 'Thumbnail')}
                    </span>
                    <span className={styles.thumbnailSize}>
                      {selectedImageFile
                        ? `${(selectedImageFile.size / (1024 * 1024)).toFixed(2)} MB`
                        : t('phishingTemplateManager.prefilledForm.imagePreview')}
                    </span>
                  </div>
                </div>

                {isSuperAdmin && (
                  <div className={styles.thumbnailActions}>
                    <button
                      type="button"
                      onClick={handleTriggerFileInput}
                      className={styles.replaceButton}
                    >
                      {t('phishingTemplateManager.prefilledForm.replaceFile')}
                    </button>
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className={styles.deleteButton}
                      title={t('delete')}
                    >
                      <Icon type="trash" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {errors.image_path && <span className={styles.fieldError}>{errors.image_path}</span>}
          </div>
        </div>

        <div className={styles.fieldGroup}>
          <div className={styles.subSectionTitle}>
            {t('phishingTemplateManager.prefilledForm.systemAndCategorization')}
          </div>
          <div className={styles.readOnlyPanel}>
            <div className={styles.readOnlyItem}>
              <span className={styles.readOnlyLabel}>
                {t('phishingTemplateManager.prefilledForm.tenantAssigned')}
              </span>
              <span className={styles.readOnlyValue}>{tenantAssigned}</span>
            </div>
            <div className={styles.readOnlyItem}>
              <span className={styles.readOnlyLabel}>
                {t('phishingTemplateManager.prefilledForm.availableLanguages')}
              </span>
              <span className={styles.readOnlyValue}>{languagesList}</span>
            </div>
          </div>
        </div>

        <div className={styles.fieldGroup}>
          <div className={styles.subSectionTitle}>
            {t('phishingTemplateManager.prefilledForm.sendingAndPhishingInfo')}
          </div>
          <div className={styles.readOnlyRowPanel}>
            <div className={styles.readOnlyItemFlex}>
              <span className={styles.readOnlyLabel}>
                {t('phishingTemplateManager.prefilledForm.sendingProfileId')}
              </span>
              <span className={styles.readOnlyValue}>{sendingProfileName}</span>
            </div>
            <div className={styles.readOnlyItemFlex}>
              <span className={styles.readOnlyLabel}>
                {t('phishingTemplateManager.prefilledForm.phishingDomainId')}
              </span>
              <span className={styles.readOnlyValue}>{phishingDomainName}</span>
            </div>
          </div>
        </div>

        {isPublished ? (
          isMobile ? (
            <div className={styles.actionRowPublishedMobile}>
              {isSuperAdmin && (
                <button
                  type="button"
                  onClick={handleOpenSaveChangesModal}
                  disabled={!isFormDirty || !isFormValid || isSubmitting}
                  className={`${styles.btnPrimary} ${styles.fullWidth}`}
                >
                  {t('phishingTemplateManager.prefilledForm.save')}
                </button>
              )}

              {isSuperAdmin && (
                <button
                  type="button"
                  onClick={handleOpenUnpublishModal}
                  disabled={isSubmitting}
                  className={`${styles.btnWarning} ${styles.fullWidth}`}
                >
                  {t('phishingTemplateManager.prefilledForm.unpublish')}
                </button>
              )}

              <button
                type="button"
                onClick={() => onBack()}
                className={`${styles.btnSecondary} ${styles.fullWidth}`}
              >
                {t('phishingManager.back')}
              </button>
            </div>
          ) : (
            <div className={styles.actionRowPublishedDesktop}>
              <div>
                {isSuperAdmin && (
                  <button
                    type="button"
                    onClick={handleOpenUnpublishModal}
                    disabled={isSubmitting}
                    className={styles.btnWarning}
                  >
                    {t('phishingTemplateManager.prefilledForm.unpublish')}
                  </button>
                )}
              </div>

              <div className={styles.actionGroupDesktop}>
                <button
                  type="button"
                  onClick={() => onBack()}
                  className={styles.btnSecondary}
                >
                  {t('phishingManager.back')}
                </button>

                {isSuperAdmin && (
                  <button
                    type="button"
                    onClick={handleOpenSaveChangesModal}
                    disabled={!isFormDirty || !isFormValid || isSubmitting}
                    className={styles.btnPrimary}
                  >
                    {t('phishingTemplateManager.prefilledForm.save')}
                  </button>
                )}
              </div>
            </div>
          )
        ) : (
          <div className={`${styles.actionRowUnpublished} ${isMobile ? styles.mobile : ''}`}>
            <button
              type="button"
              onClick={() => onBack()}
              className={`${styles.btnSecondary} ${isMobile ? styles.fullWidth : ''}`}
            >
              {t('phishingManager.back')}
            </button>

            {isSuperAdmin && (
              <button
                type="button"
                onClick={handleOpenPublishModal}
                disabled={!isFormValid || isSubmitting}
                className={`${styles.btnPrimary} ${isMobile ? styles.fullWidth : ''}`}
              >
                {t('phishingTemplateManager.prefilledForm.publishTemplate')}
              </button>
            )}
          </div>
        )}
      </div>

      <TemplateActionModal
        visible={isModalVisible}
        type={modalType}
        loading={isSubmitting}
        onClose={handleCloseModal}
        onConfirm={handleConfirmAction}
      />
    </div>
  );
};

export default PrefilledForm;